/**
 * Decodes a JWT payload without verifying the signature. Only use this on
 * tokens already accepted by the auth provider: the API verifies the signature
 * on every request, so the client just reads the claims it was issued.
 * Returns null when the token is not a well-formed JWT.
 */
export function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const segment = token.split('.')[1];
    if (!segment) {
      return null;
    }
    const base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '=='.slice(0, (4 - (base64.length % 4)) % 4);
    const bytes = Uint8Array.from(atob(padded), (char) => char.charCodeAt(0));
    const payload: unknown = JSON.parse(new TextDecoder().decode(bytes));
    return payload !== null && typeof payload === 'object' && !Array.isArray(payload)
      ? (payload as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
}

/**
 * True for a token Frontegg issued to someone impersonating a user: its `act` claim names the impersonator.
 * Impersonation tokens live 5 minutes, ordinary tokens much longer.
 */
export function isImpersonationToken(token: string): boolean {
  const act = decodeJwtPayload(token)?.act;
  return typeof act === 'object' && act !== null;
}

const TOKEN_EXPIRY_BUFFER_MS = 5 * 60 * 1000;
// An impersonation token lives 5 minutes, so the ordinary buffer would count it expired on arrival.
const IMPERSONATION_TOKEN_EXPIRY_BUFFER_MS = 30 * 1000;

export function isTokenExpired(accessToken: string): boolean {
  const exp = decodeJwtPayload(accessToken)?.exp;
  if (typeof exp !== 'number') {
    return true;
  }
  const bufferMs = isImpersonationToken(accessToken)
    ? IMPERSONATION_TOKEN_EXPIRY_BUFFER_MS
    : TOKEN_EXPIRY_BUFFER_MS;
  return exp * 1000 < Date.now() + bufferMs;
}
